import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, FileSignature, MapPin, Plus, FileDown } from "lucide-react";
import { JobSiteFormDialog } from "@/components/job-sites/JobSiteFormDialog";
import { ProjectMaterialsCard } from "@/components/materials/ProjectMaterialsCard";
import { generateContractPdf } from "@/utils/reports.functions";
import { downloadPdfFromBase64 } from "@/lib/report-export";

export const Route = createFileRoute("/_app/projects/$projectId")({
  component: ProjectDetailPage,
});

type Status = "planning" | "active" | "on_hold" | "completed" | "cancelled";

type Project = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: Status;
  start_date: string | null;
  end_date: string | null;
  budget: number;
  currency: string;
  notes: string | null;
  client_id: string;
  contract_id: string | null;
  clients: { name: string } | null;
  contracts: { id: string; number: string; title: string } | null;
};

const variant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  planning: "secondary",
  active: "default",
  on_hold: "outline",
  completed: "outline",
  cancelled: "destructive",
};

function ProjectDetailPage() {
  const { t } = useTranslation();
  const { projectId } = Route.useParams();
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = hasRole("admin");
  const generateContractPdfFn = useServerFn(generateContractPdf);
  const [downloadingContract, setDownloadingContract] = useState(false);

  const handleDownloadContract = async (contractId: string, contractNumber: string) => {
    setDownloadingContract(true);
    try {
      const { pdfBase64, filename } = await generateContractPdfFn({ data: { contractId } });
      downloadPdfFromBase64(filename || `contrato-${contractNumber}.pdf`, pdfBase64);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("projectDetail.toasts.contractPdfFail"));
    } finally {
      setDownloadingContract(false);
    }
  };

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sites, setSites] = useState<
    Array<{
      id: string;
      name: string;
      city: string | null;
      state: string | null;
      address_line1: string | null;
      contact_name: string | null;
    }>
  >([]);
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);

  async function loadSites() {
    const { data } = await supabase
      .from("job_sites")
      .select("id, name, city, state, address_line1, contact_name")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });
    setSites(data ?? []);
  }

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select(
        "id, code, name, description, status, start_date, end_date, budget, currency, notes, client_id, contract_id, clients(name), contracts(id, number, title)",
      )
      .eq("id", projectId)
      .maybeSingle();
    setLoading(false);
    if (error) return toast.error(error.message);
    setProject(data as unknown as Project);
  }

  useEffect(() => {
    load();
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function changeStatus(next: Status) {
    if (!project) return;
    setUpdating(true);
    const { error } = await supabase.from("projects").update({ status: next }).eq("id", project.id);
    setUpdating(false);
    if (error) return toast.error(error.message);
    setProject({ ...project, status: next });
    toast.success(t("projectDetail.toasts.statusUpdated"));
  }

  async function onDelete() {
    if (!project) return;
    const { error } = await supabase.from("projects").delete().eq("id", project.id);
    if (error) return toast.error(error.message);
    toast.success(t("projectDetail.toasts.deleted"));
    navigate({ to: "/projects" });
  }

  const fmt = (n: number, cur: string) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(n ?? 0);

  if (loading) {
    return (
      <div className="py-10 text-center text-muted-foreground">{t("projectDetail.loading")}</div>
    );
  }
  if (!project) {
    return (
      <div className="space-y-3 py-10 text-center">
        <p className="text-muted-foreground">{t("projectDetail.notFound")}</p>
        <Button asChild variant="outline">
          <Link to="/projects">{t("projectDetail.backToList")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/projects">
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t("projectDetail.back")}
            </Link>
          </Button>
          <div>
            <p className="font-mono text-xs text-muted-foreground">{project.code}</p>
            <h1 className="text-2xl font-semibold tracking-tight">{project.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={variant[project.status]}>
            {t(`projectDetail.statuses.${project.status}`)}
          </Badge>
          <Select
            value={project.status}
            onValueChange={(v) => changeStatus(v as Status)}
            disabled={updating}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="planning">{t("projectDetail.statuses.planning")}</SelectItem>
              <SelectItem value="active">{t("projectDetail.statuses.active")}</SelectItem>
              <SelectItem value="on_hold">{t("projectDetail.statuses.on_hold")}</SelectItem>
              <SelectItem value="completed">{t("projectDetail.statuses.completed")}</SelectItem>
              <SelectItem value="cancelled">{t("projectDetail.statuses.cancelled")}</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("projectDetail.deleteTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("projectDetail.deleteDesc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("projectDetail.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete}>
                    {t("projectDetail.delete")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t("projectDetail.overview")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("projectDetail.client")} value={project.clients?.name ?? "—"} />
              <Field
                label={t("projectDetail.contract")}
                value={
                  project.contracts ? (
                    <Link
                      to="/contracts/$contractId"
                      params={{ contractId: project.contracts.id }}
                      className="text-primary hover:underline"
                    >
                      {project.contracts.number} — {project.contracts.title}
                    </Link>
                  ) : (
                    "—"
                  )
                }
              />
              <Field label={t("projectDetail.startDate")} value={project.start_date ?? "—"} />
              <Field label={t("projectDetail.endDate")} value={project.end_date ?? "—"} />
            </div>
            {project.description && (
              <div>
                <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
                  {t("projectDetail.description")}
                </p>
                <p className="whitespace-pre-wrap">{project.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("projectDetail.budget")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-3xl font-semibold tracking-tight">
              {fmt(Number(project.budget), project.currency)}
            </p>
            <div className="flex flex-col gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/invoices/new"
                  search={project.contract_id ? { contractId: project.contract_id } : {}}
                >
                  <Receipt className="mr-2 h-4 w-4" />
                  {t("projectDetail.createInvoice")}
                </Link>
              </Button>
              {project.contracts ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleDownloadContract(project.contracts!.id, project.contracts!.number)
                  }
                  disabled={downloadingContract}
                >
                  <FileDown className="mr-2 h-4 w-4" />
                  {downloadingContract
                    ? t("projectDetail.downloadingContract")
                    : t("projectDetail.downloadContract")}
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/contracts/new">
                    <FileSignature className="mr-2 h-4 w-4" />
                    {t("projectDetail.createContract")}
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{t("projectDetail.jobSites")}</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setSiteDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("projectDetail.addJobSite")}
          </Button>
        </CardHeader>
        <CardContent>
          {sites.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
              <MapPin className="h-7 w-7" />
              <p className="text-sm">{t("projectDetail.noJobSites")}</p>
            </div>
          ) : (
            <ul className="divide-y">
              {sites.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <Link
                      to="/job-sites/$jobSiteId"
                      params={{ jobSiteId: s.id }}
                      className="font-medium hover:underline"
                    >
                      {s.name}
                    </Link>
                    <p className="truncate text-xs text-muted-foreground">
                      {[s.address_line1, s.city, s.state].filter(Boolean).join(", ") ||
                        t("projectDetail.noAddress")}
                      {s.contact_name ? ` · ${s.contact_name}` : ""}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/job-sites/$jobSiteId" params={{ jobSiteId: s.id }}>
                      {t("projectDetail.view")}
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ProjectMaterialsCard projectId={project.id} projectCurrency={project.currency} />

      <JobSiteFormDialog
        open={siteDialogOpen}
        onOpenChange={setSiteDialogOpen}
        projectId={project.id}
        onSaved={loadSites}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium">{value}</p>
    </div>
  );
}
